import React from 'react';
import { Route, Redirect, Switch } from 'react-router-dom';
import { Provider } from 'react-redux';
import { ConnectedRouter } from 'connected-react-router';
import errorPages, { HeadlessErrorPage, NotFound } from '@saucelabs/sl-component-error-pages';
import AnalyticsRoutes from '@saucelabs/web-ui-analytics/src/routes';
import AppManagementRoutes from '@saucelabs/web-ui-app-management/src/routes';
import ArchivesRoutes from '@saucelabs/web-ui-archives/src/routes';
import AuthRoutes from '@saucelabs/web-ui-auth/src/routes';
import BillingUnifiedRoutes from '@saucelabs/web-ui-billing-unified/src/routes';
import BuildsRoutes from '@saucelabs/web-ui-builds/src/routes';
import PerformanceRoutes from '@saucelabs/web-ui-extended-debugging/src/routes';
import IntegrationsRoutes from '@saucelabs/web-ui-integrations/src/routes';
import InvitationsRoutes from '@saucelabs/web-ui-invitations/src/routes';
import JobRoutes from '@saucelabs/web-ui-job-details/src/routes';
import EmbeddedJobRoutes from '@saucelabs/web-ui-job-details/src/routes-embedded';
import JobsRoutes from '@saucelabs/web-ui-jobs/src/routes';
import JobsOpenSauceRoutes from '@saucelabs/web-ui-jobs/src/routesOpenSauce';
import HealthCheckRoutes from '@saucelabs/web-ui-health-check/src/routes';
import GettingStartedRoutes from '@saucelabs/web-ui-getting-started/src/routes';
import ManualRoutes from '@saucelabs/web-ui-manual/src/routes';
import ManualFullscreenRoutes from '@saucelabs/web-ui-manual/src/routesFullscreen';
import ManualPublicRoutes from '@saucelabs/web-ui-manual/src/routesPublic';
import PlatformConfiguratorRoutes from '@saucelabs/web-ui-platform-configurator/src/routes';
import TeamManagementRoutes from '@saucelabs/web-ui-team-management/src/routes';
import TunnelsRoutes from '@saucelabs/web-ui-tunnels/src/routes';
import UserSettingsRoutes from '@saucelabs/web-ui-user-settings/src/routes';
import VisualRoutes from '@saucelabs/web-ui-visual/src/routes';
import { isSupportedFeature, constants as features } from '@saucelabs/sl-component-supported-feature';
import { LastLocationProvider } from 'react-router-last-location';
import { ModalProvider } from '@saucelabs/ds-modal';

import DashboardRoute from '../src/dashboard/containers/DashboardRoute';
import ApplicationContainer from './application/containers/ApplicationContainer';
import store, { browserHistory } from './store';
import { SkipMenu } from './dashboard/components/SkipMenu';

const EmbeddedHeadlessErrorPage = () => <HeadlessErrorPage embedded />;
const isSupportedFeatureRoute = ({ name, routes }) => (
  isSupportedFeature(name) ? routes : EmbeddedHeadlessErrorPage
);

const router = (
  <Provider store={store}>
    <SkipMenu />
    <ModalProvider>
      <ConnectedRouter history={browserHistory}>
        <LastLocationProvider>
          <Switch>
            {Object.entries(errorPages).map(([code, ErrorPage]) => (
              <Route
                key={`error-${code}`}
                component={ErrorPage}
                path={`/${code}`}
              />
            ))}
            <Redirect
              exact
              from="/"
              to="/dashboard/tests"
            />
            {/* legacy Open Sauce route */}
            <Redirect
              from="/u/:username"
              to="/open_sauce/user/:username"
            />
            {/* legacy live testing routes */}
            <Redirect
              exact
              from="/dashboard/manual"
              to="/dashboard/live"
            />
            {/* legacy onboarding pages */}
            <Redirect
              from="/onboarding/"
              to="/getting-started"
            />
            <Route
              component={AuthRoutes}
              path={[
                '/verify-email',
                '/verify-user',
              ]}
              viewName="Authentication"
            />
            <Route
              component={InvitationsRoutes}
              path="/invitations/"
              viewName="Invitation"
            />
            <Route
              component={HealthCheckRoutes}
              path="/health-check"
              viewName="Health Check"
            />
            {ManualPublicRoutes}
            <Route
              component={EmbeddedJobRoutes}
              path={[
                '/job-embed',
                '/video-embed',
              ]}
              viewName="Embeded Job Details"
            />
            <ApplicationContainer>
              <Switch>
                <Route
                  component={AuthRoutes}
                  path="/email-verification"
                  viewName="Email Verification"
                />
                <DashboardRoute
                  component={JobsOpenSauceRoutes}
                  path="/open_sauce/"
                  viewName="Dashboard"
                />
                <DashboardRoute
                  component={GettingStartedRoutes}
                  path="/getting-started"
                  viewName="Get Started Guide"
                />
                <DashboardRoute
                  component={TeamManagementRoutes}
                  path="/team-management"
                  viewName="Team Management"
                />
                <DashboardRoute
                  component={isSupportedFeatureRoute({
                    name: features.ANALYTICS,
                    routes: AnalyticsRoutes,
                  })}
                  path="/analytics"
                  viewName="Analytics"
                />
                <DashboardRoute
                  component={isSupportedFeatureRoute({
                    name: features.ARCHIVES,
                    routes: ArchivesRoutes,
                  })}
                  path="/archives"
                  viewName="Archives"
                />
                <DashboardRoute
                  component={PlatformConfiguratorRoutes}
                  path="/platform-configurator"
                  viewName="Platform Configurator"
                />
                <DashboardRoute
                  component={VisualRoutes}
                  path="/visual"
                  viewName="Visual"
                />
                <Route
                  component={BillingUnifiedRoutes}
                  path="/billing"
                />
                <DashboardRoute
                  component={BuildsRoutes}
                  path="/builds/"
                  viewName="Builds"
                />
                <DashboardRoute
                  component={JobsRoutes}
                  path="/dashboard"
                  viewName="Dashboard"
                />
                <DashboardRoute
                  component={isSupportedFeatureRoute({
                    name: features.INTEGRATIONS,
                    routes: IntegrationsRoutes,
                  })}
                  path="/integrations"
                  viewName="Integrations"
                />
                <DashboardRoute
                  component={JobRoutes}
                  path="/tests/"
                  viewName="Job Details"
                />
                <DashboardRoute
                  component={PerformanceRoutes}
                  path="/performance/"
                  viewName="Performance"
                />
                <DashboardRoute
                  component={TunnelsRoutes}
                  path="/tunnels"
                  viewName="Tunnels"
                />
                <DashboardRoute
                  component={UserSettingsRoutes}
                  path="/user-settings"
                  viewName="UserSettings"
                />
                {isSupportedFeature(features.APP_MANAGEMENT) && (
                  <DashboardRoute
                    component={AppManagementRoutes}
                    path="/app-management"
                    viewName="AppManagement"
                  />
                )}
                <Route
                  component={ManualFullscreenRoutes}
                  path={[
                    '/live/mobile/dataCenters/:dataCenterId/devices/:deviceId',
                    '/live/lsm/:sessionId?',
                  ]}
                />
                {isSupportedFeature(features.LIVE_TESTING) ? (
                  <DashboardRoute
                    component={ManualRoutes}
                    path="/live"
                    viewName="Live testing"
                  />
                ) : (
                  <DashboardRoute
                    component={EmbeddedHeadlessErrorPage}
                    path="/live"
                    viewName="Live testing"
                  />
                )}
                {/* Render 404 error page if there is no Route match */}
                <Route component={NotFound} />
              </Switch>
            </ApplicationContainer>
          </Switch>
        </LastLocationProvider>
      </ConnectedRouter>
    </ModalProvider>
  </Provider>
);

export default router;
